import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CommitteeAgenda } from '../../lib/types';
import { useApiQuery, useCursorList } from '../../lib/useApi';
import { BackLink, EmptyState, ErrorNote, Field, LoadMoreButton, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function CommitteeAgendas() {
  const { persona } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [showNew, setShowNew] = useState(false);
  const canCreate = persona?.roles.includes('COMMITTEE_SECRETARIAT') ?? false;

  const basePath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '25');
    if (status) params.set('status', status);
    return `/api/v1/m17/committee-agendas?${params.toString()}`;
  }, [status]);
  const list = useCursorList<CommitteeAgenda>(basePath);

  return (
    <div>
      <PageHeader
        title="Planning Committee"
        subtitle="Agendas auto-built from decision-ready applications (ASSESSMENT state with a recommendation on file)."
        actions={canCreate ? <button className="btn-primary" onClick={() => setShowNew(true)}>+ New agenda</button> : undefined}
      />

      <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
        <Field label="Status" className="w-48">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="CLOSED">Closed</option>
          </select>
        </Field>
      </div>

      {list.error ? <div className="mb-4"><ErrorNote message={list.error} /></div> : null}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[500px] border-collapse">
          <thead>
            <tr>
              <th className="table-head">Meeting date</th>
              <th className="table-head">Status</th>
              <th className="table-head">Items</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((a) => (
              <tr key={a.id} className="table-row-link" onClick={() => navigate(`/planning/committee/${a.id}`)}>
                <td className="table-cell font-semibold text-slate-800">{formatDate(a.meetingDate)}</td>
                <td className="table-cell"><StatusBadge status={a.status} /></td>
                <td className="table-cell text-xs text-slate-500">{a.items?.length ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.loading ? <Spinner label="Loading agendas…" /> : null}
        {!list.loading && list.items.length === 0 ? <EmptyState title="No committee agendas found" /> : null}
      </div>
      <LoadMoreButton nextCursor={list.nextCursor} loading={list.loading} onClick={list.loadMore} />

      {showNew ? (
        <NewAgendaModal
          onClose={() => setShowNew(false)}
          onCreated={(agenda) => {
            setShowNew(false);
            navigate(`/planning/committee/${agenda.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function NewAgendaModal({ onClose, onCreated }: { onClose: () => void; onCreated: (agenda: CommitteeAgenda) => void }) {
  const [meetingDate, setMeetingDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const agenda = await api.post<CommitteeAgenda>('/api/v1/m17/committee-agendas', { meetingDate });
      onCreated(agenda);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not create agenda');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New committee agenda" onClose={onClose}>
      <p className="mb-3 text-xs text-slate-500">
        Every ASSESSMENT-state application with a recommendation on file, not already on another open agenda, is
        swept in automatically.
      </p>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <Field label="Meeting date">
        <input className="field-input" type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} />
      </Field>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy || !meetingDate} onClick={submit}>
          {busy ? 'Creating…' : 'Create agenda'}
        </button>
      </div>
    </Modal>
  );
}

export function CommitteeAgendaDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const canDecide = persona?.roles.includes('COMMITTEE_SECRETARIAT') ?? false;
  const agendaQ = useApiQuery<CommitteeAgenda>(`/api/v1/m17/committee-agendas/${id}`);
  const [decidingItem, setDecidingItem] = useState<string | null>(null);
  const [decision, setDecision] = useState<'APPROVED' | 'REFUSED'>('APPROVED');
  const [conditions, setConditions] = useState('');
  const [reasons, setReasons] = useState('');
  const [minutes, setMinutes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decide = async (itemId: string) => {
    setBusy(true);
    setError(null);
    try {
      const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
      await api.post(`/api/v1/m17/committee-agendas/${id}/items/${itemId}/decide`, {
        decision,
        conditions: list(conditions),
        reasons: list(reasons),
      });
      setDecidingItem(null);
      setConditions('');
      setReasons('');
      agendaQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record decision');
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m17/committee-agendas/${id}/close`, { minutes });
      agendaQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not close agenda');
    } finally {
      setBusy(false);
    }
  };

  if (agendaQ.loading) return <Spinner label="Loading agenda…" />;
  if (agendaQ.error || !agendaQ.data) return <ErrorNote message={agendaQ.error ?? 'Agenda not found'} />;
  const agenda = agendaQ.data;
  const undecided = (agenda.items ?? []).filter((i) => !i.decision).length;

  return (
    <div>
      <BackLink to="/planning/committee" />
      <PageHeader title={`Agenda — ${formatDate(agenda.meetingDate)}`} actions={<StatusBadge status={agenda.status} />} />
      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}

      <div className="space-y-3">
        {(agenda.items ?? []).map((item) => (
          <div key={item.id} className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-bold text-slate-800">{item.application?.refNo ?? item.applicationId}</span>
                {item.application ? <span className="ml-2 text-xs text-slate-500">{item.application.classCode.replaceAll('_', ' ')} · parcel {item.application.parcelNo}</span> : null}
              </div>
              {item.decision ? <StatusBadge status={item.decision} /> : <StatusBadge status="PENDING" />}
            </div>
            {item.application ? <p className="mt-1 text-xs text-slate-500">{item.application.proposal.description}</p> : null}
            {canDecide && agenda.status === 'DRAFT' && !item.decision ? (
              decidingItem === item.id ? (
                <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Field label="Decision">
                      <select className="field-input" value={decision} onChange={(e) => setDecision(e.target.value as 'APPROVED' | 'REFUSED')}>
                        <option value="APPROVED">Approved</option>
                        <option value="REFUSED">Refused</option>
                      </select>
                    </Field>
                    <Field label="Conditions (comma-separated)">
                      <input className="field-input" value={conditions} onChange={(e) => setConditions(e.target.value)} />
                    </Field>
                  </div>
                  <Field label="Reasons (required if refusing)">
                    <input className="field-input" value={reasons} onChange={(e) => setReasons(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-primary btn-xs" disabled={busy} onClick={() => decide(item.id)}>Save decision</button>
                    <button className="btn-secondary btn-xs" onClick={() => setDecidingItem(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button className="btn-secondary btn-xs mt-2" onClick={() => setDecidingItem(item.id)}>Record decision</button>
              )
            ) : null}
          </div>
        ))}
        {(agenda.items ?? []).length === 0 ? <EmptyState title="No decision-ready applications were swept onto this agenda" /> : null}
      </div>

      {canDecide && agenda.status === 'DRAFT' ? (
        <div className="card mt-5 p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Close agenda</h2>
          {undecided > 0 ? <p className="mb-2 text-xs text-amber-600">{undecided} item(s) still undecided — the API rejects closing until every item has a decision.</p> : null}
          <Field label="Minutes">
            <textarea className="field-input" rows={4} value={minutes} onChange={(e) => setMinutes(e.target.value)} />
          </Field>
          <button className="btn-primary mt-3" disabled={busy || !minutes} onClick={close}>
            Close agenda
          </button>
        </div>
      ) : null}

      {agenda.status === 'CLOSED' && agenda.minutes ? (
        <div className="card mt-5 p-5">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Minutes</h2>
          <p className="whitespace-pre-wrap text-sm text-slate-600">{agenda.minutes}</p>
        </div>
      ) : null}
    </div>
  );
}
